<template>
  <ModalComponent
    :submitButtonText="$t('forwards.deals.metadata.save')"
    :hideSubmitButton="true"
    size="lg"
    @close="close"
    @setForceToggle="setForceToggle"
  >
    <template #title>{{ $t("forwards.deals.metadata.edit_title") }}</template>

    <template #body>
      <div class="p-1">
        <p
          v-if="shippedCount > 0"
          class="advisory-note mb-3"
          role="status"
          data-testid="shipped-deliveries-warning"
        >
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{
            $t("forwards.deals.metadata.shipped_keep_details", shippedCount)
          }}</span>
        </p>

        <div class="mb-3">
          <label class="form-label" :for="nameId">{{
            $t("forwards.deals.metadata.name")
          }}</label>
          <input
            :id="nameId"
            v-model="name"
            type="text"
            class="form-control"
            :maxlength="NAME_MAX"
            :class="{ 'is-invalid': name.trim() === '' }"
          />
        </div>

        <div class="mb-3">
          <label class="form-label" :for="descriptionId">{{
            $t("forwards.deals.metadata.description")
          }}</label>
          <textarea
            :id="descriptionId"
            v-model="description"
            class="form-control"
            rows="2"
            :aria-describedby="descriptionHelpId"
          ></textarea>
          <div :id="descriptionHelpId" class="form-text">
            {{ $t("forwards.common.deal_text.description_help") }}
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label" :for="internalNotesId">{{
            $t("forwards.common.deal_text.internal_notes")
          }}</label>
          <textarea
            :id="internalNotesId"
            v-model="internalNotes"
            class="form-control"
            rows="3"
            :maxlength="INTERNAL_NOTES_MAX"
            :aria-describedby="internalNotesHelpId"
            data-testid="deal-internal-notes-input"
          ></textarea>
          <div :id="internalNotesHelpId" class="form-text">
            {{ $t("forwards.common.deal_text.internal_notes_help") }}
          </div>
        </div>

        <CounterpartyPicker
          v-model="counterpartyModel"
          :registrationAuthority="registrationAuthority"
          @select:company="handleCompanySelected"
        />

        <div class="row g-3 mt-0">
          <div class="col-md-6">
            <label class="form-label" :for="authorityId">{{
              $t("forwards.deals.metadata.registration_authority")
            }}</label>
            <select
              :id="authorityId"
              v-model="registrationAuthority"
              class="form-select"
            >
              <option
                v-for="authority in AUTHORITIES"
                :key="authority"
                :value="authority"
              >
                {{ authority }}
              </option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label" :for="countryId">{{
              $t("forwards.deals.metadata.billing_country")
            }}</label>
            <input
              :id="countryId"
              v-model="counterpartyCountry"
              type="text"
              class="form-control"
              :maxlength="COUNTRY_MAX"
            />
          </div>
          <div class="col-12">
            <label class="form-label" :for="emailsId">{{
              $t("forwards.deals.metadata.emails")
            }}</label>
            <input
              :id="emailsId"
              v-model="counterpartyEmails"
              type="text"
              class="form-control"
              :maxlength="EMAILS_MAX"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label" :for="signedId">{{
              $t("forwards.deals.metadata.signed_at")
            }}</label>
            <input
              :id="signedId"
              v-model="signedAt"
              type="date"
              class="form-control"
            />
          </div>
          <div v-if="!isPayAsProduced" class="col-md-6">
            <label class="form-label" :for="volumeId">{{
              $t("forwards.deals.metadata.deal_volume")
            }}</label>
            <MwhInputComponent
              :id="volumeId"
              v-model="volumeMwh"
              :min="0"
              :invalid="volumeMwh === null"
            />
          </div>
          <div class="col-12">
            <label class="form-label" :for="contractId">{{
              $t("forwards.deals.metadata.contract_links")
            }}</label>
            <input
              :id="contractId"
              v-model="contractLinks"
              type="text"
              class="form-control"
              :maxlength="LINKS_MAX"
            />
          </div>
          <div class="col-12">
            <label class="form-label" :for="transactionId">{{
              $t("forwards.deals.metadata.transaction_link")
            }}</label>
            <input
              :id="transactionId"
              v-model="transactionLink"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': !transactionLinkValid }"
              :maxlength="TRANSACTION_LINK_MAX"
            />
            <div v-if="!transactionLinkValid" class="invalid-feedback">
              {{ $t("forwards.deals.metadata.transaction_link_invalid") }}
            </div>
          </div>
          <div class="col-12">
            <label class="form-label" :for="offeringSheetsId">{{
              $t("forwards.deals.metadata.offering_sheets")
            }}</label>
            <textarea
              :id="offeringSheetsId"
              v-model="volumesSpecificationLinks"
              class="form-control"
              rows="2"
              :maxlength="LINKS_MAX"
            ></textarea>
            <div class="form-text">
              {{ $t("forwards.deals.metadata.offering_sheets_help") }}
            </div>
          </div>
        </div>

        <p v-if="errorMessage" class="error-note mt-3 mb-0" role="alert">
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{ errorMessage }}</span>
        </p>
      </div>
    </template>

    <template #footer>
      <button type="button" class="btn btn-link text-secondary" @click="close">
        {{ $t("forwards.deals.common.cancel") }}
      </button>
      <ActionButtonWithSpinner
        :busy="busy"
        :disabled="busy || !isComplete || !transactionLinkValid"
        variant="btn-primary"
        @click="submit"
      >
        {{ $t("forwards.deals.metadata.save") }}
      </ActionButtonWithSpinner>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import CounterpartyPicker from "@/components/common/CounterpartyPicker.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import MwhInputComponent from "@/forwards2/components/MwhInputComponent.vue";
import { isSingleWebLinkOrBlank } from "@/forwards2/deals/dealLinks";
import { isDeliveryShipped } from "@/forwards2/deals/deliveryFrozen";
import { INTERNAL_NOTES_MAX } from "@/forwards2/picker/pickerForm";
import type {
  DealMetadataRequest,
  DealRegistrationAuthority,
  DealResponse,
} from "@/forwards2/forwardsTypes";
import { useCounterpartyForm } from "@/components/common/useCounterpartyForm";
import { computed, ref, useId, watch } from "vue";

/** `app/registry/registration_authorities.DELIVERABLE_AUTHORITIES`. */
const AUTHORITIES: DealRegistrationAuthority[] = ["AIB", "REGO", "RGP"];

// The SalesTransaction columns the deal's metadata is synced into (deal_service).
// `NAME_MAX` is `constants.DEAL_NAME_MAX`, which every deal-creating path applies too.
const NAME_MAX = 128;
const COUNTRY_MAX = 128;
const EMAILS_MAX = 1000;
const LINKS_MAX = 1024;
const TRANSACTION_LINK_MAX = 256;

/**
 * The deal's buyer and contract details.
 *
 * The same `CounterpartyPicker` the sales-transaction form uses, so the buyer identity, the EECS
 * account and the `global_company` link behave identically on both screens — the values are copied
 * onto every undelivered delivery's transaction, which is why the field lengths are the
 * transaction's rather than the deal's.
 */
const props = withDefaults(
  defineProps<{
    deal: DealResponse;
    busy?: boolean;
    errorMessage?: string | null;
  }>(),
  { busy: false, errorMessage: null }
);

const emit = defineEmits<{
  submit: [body: DealMetadataRequest];
}>();

const model = defineModel<boolean>({ required: true });

const nameId = useId();
const descriptionId = useId();
const descriptionHelpId = useId();
const internalNotesId = useId();
const internalNotesHelpId = useId();
const authorityId = useId();
const countryId = useId();
const emailsId = useId();
const signedId = useId();
const volumeId = useId();
const contractId = useId();
const transactionId = useId();
const offeringSheetsId = useId();

const forceToggle = ref<(value: boolean) => void>();

/**
 * `ModalComponent` hands its toggle over during its own setup, before any watcher has run, so the
 * current model value is applied at once — otherwise a modal mounted already open stays hidden.
 */
const setForceToggle = (toggle: (value: boolean) => void) => {
  forceToggle.value = toggle;
  toggle(model.value);
};

const {
  counterpartyName,
  counterpartyCountry,
  counterpartyEecsAccountNumber,
  counterpartyEmails,
  registrationAuthority,
  counterpartyModel,
  selectedGlobalCompany,
  handleCompanySelected,
  hydrateLinkedCompany,
} = useCounterpartyForm();

/**
 * A pay-as-produced deal sells what is produced, so it carries no fixed volume and the service
 * refuses one. The field is dropped rather than disabled, and left out of the body (RV-dealpage-02).
 */
const isPayAsProduced = computed(() => props.deal.type === "pay_as_produced");

const name = ref("");
const description = ref("");
const internalNotes = ref("");
const signedAt = ref("");
const volumeMwh = ref<number | null>(null);
const contractLinks = ref("");
const transactionLink = ref("");
const volumesSpecificationLinks = ref("");

/**
 * A fixed-volume deal's volume is its headline number and the column is NOT NULL for that type, so
 * an emptied field is a refused save rather than "not set" (RV2-plan-editing-02).
 */
const isComplete = computed(
  () =>
    name.value.trim() !== "" &&
    (isPayAsProduced.value || volumeMwh.value !== null)
);

// The backend refuses any other transaction link, and a refusal of the request shape reaches no field
// of this form — only the generic error toast — so the form names the field itself.
const transactionLinkValid = computed(() =>
  isSingleWebLinkOrBlank(transactionLink.value)
);

const hydrate = () => {
  const deal = props.deal;
  name.value = deal.name;
  description.value = deal.description ?? "";
  internalNotes.value = deal.internal_notes ?? "";
  signedAt.value = deal.signed_at ?? "";
  volumeMwh.value = deal.volume_mwh;
  contractLinks.value = deal.contract_links ?? "";
  transactionLink.value = deal.transaction_link ?? "";
  volumesSpecificationLinks.value = deal.volumes_specification_links ?? "";
  counterpartyName.value = deal.counterparty_name ?? "";
  counterpartyCountry.value = deal.counterparty_country ?? "";
  counterpartyEecsAccountNumber.value =
    deal.counterparty_eecs_account_number ?? "";
  counterpartyEmails.value = deal.counterparty_emails ?? "";
  registrationAuthority.value = deal.counterparty_registration_authority;
  hydrateLinkedCompany({
    counterparty_name: deal.counterparty_name,
    global_company_id: deal.counterparty_global_company_id,
  });
};

watch(model, (isOpen) => {
  forceToggle.value?.(isOpen);
  if (isOpen) hydrate();
});

const close = () => {
  model.value = false;
};

const blankToNull = (value: string) => (value.trim() === "" ? null : value);

/** Shipped transactions keep the metadata they went out with: the sync skips them, and so do their invoices. */
const shippedCount = computed(
  () =>
    props.deal.deliveries.filter((delivery) =>
      isDeliveryShipped(delivery.transaction_status)
    ).length
);

const submit = () => {
  emit("submit", {
    name: name.value,
    description: blankToNull(description.value),
    internal_notes: blankToNull(internalNotes.value),
    signed_at: blankToNull(signedAt.value),
    ...(isPayAsProduced.value ? {} : { volume_mwh: volumeMwh.value }),
    counterparty_name: blankToNull(counterpartyName.value),
    counterparty_country: blankToNull(counterpartyCountry.value),
    counterparty_eecs_account_number: blankToNull(
      counterpartyEecsAccountNumber.value
    ),
    counterparty_emails: blankToNull(counterpartyEmails.value),
    counterparty_registration_authority: registrationAuthority.value,
    counterparty_global_company_id: selectedGlobalCompany.value?.id ?? null,
    contract_links: blankToNull(contractLinks.value),
    transaction_link: blankToNull(transactionLink.value),
    volumes_specification_links: blankToNull(volumesSpecificationLinks.value),
  });
};
</script>
